/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit'
import { get, sortBy, omit } from 'lodash'

import { POLLING_INTERVALS } from 'components/route-manager/RouteManagerAppBar/polling/settings'
import { deserializeDate, formatDateToBEFormatDateFns, serializeDate } from 'utils/date'

export const VIEW_MODE_SEQUENCE = 'board'
export const VIEW_MODE_MAP = 'map'
export const VIEW_MODE_MAP_V2 = 'clustermap'
export const VIEW_MODE_BOARD_V2 = 'boardv2'
export const VIEW_MODE_LIST = 'list'
export const VIEW_MODE_PRODUCTIVITY = 'productivity'
export const DEFAULT_VIEW_MODE = VIEW_MODE_BOARD_V2

// Every value `?view=` and `changeViewMode` accept. An unknown one silently
// becomes the default, so a new tab has to be listed here AND in NewRoutesPage's
// URL check or it never opens.
export const ROUTE_VIEW_MODES = [
  VIEW_MODE_SEQUENCE,
  VIEW_MODE_MAP,
  VIEW_MODE_MAP_V2,
  VIEW_MODE_BOARD_V2,
  VIEW_MODE_LIST,
  VIEW_MODE_PRODUCTIVITY,
]
export const WO_COLOR_STOPS_LAYER = 'workorder-stops-layer'
export const ROUTE_COLOR_STOPS_LAYER = 'route-color-stops-layer'
export const WO_COLOR_STOPS_DOT_LAYER = 'workorder-stops-dot-layer'

export const ROUTE_STOPS_UNASSIGNED_LAYER = 'route-stops-unassigned-layer'
export const ROUTE_STOPS_UNASSIGNED_DOT_LAYER = 'route-stops-unassigned-dot-layer'

export const ROUTE_STOPS_UNASSIGNED = `route-stops-unassigned`
export const ROUTE_STOPS = `route-stops`
export const ROUTE_STOPS_LINE_STRING = `route-stops-line-string`
export const ROUTE_COLOR_STOPS_DOT_LAYER = 'route-color-stops-dot-layer'

const POLLING_DEFAULT_STATE = { autoPoll: false, interval: POLLING_INTERVALS.FIVE.value, lastRefetchTime: null }

export const getStopDetailsPopoverClosedState = () => ({
  isOpen: false,
  id: '',
  position: null,
  stop: {},
  isHovered: false,
  isMarkerHovered: false,
})

const getInitialState = () => ({
  isUnassignedStopsOpen: false,
  viewMode: DEFAULT_VIEW_MODE,
  allRouteIds: [],
  allRoutes: [],
  filters: {
    serializedServiceDate: serializeDate(new Date()),
    selectedRouteIds: null,
    businessId: null,
  },
  mapFilters: {
    hasVideoArtifact: null,
    exceptionTypes: null,
    withPhotos: null,
    withoutPhotos: null,
    materialIds: null,
    measureIds: null,
    actionIds: null,
    methodIds: null,
    businessLines: null,
    workOrderStatuses: null,
    tagIds: null,
    noTags: null,
  },
  map: {
    currentLayer: ROUTE_COLOR_STOPS_LAYER,
    isStreetView: true,
  },
  mapControlPanel: {
    clusterMaxZoom: 18,
    expandedClusterOffset: 4,
    labelDensityLevel: 0,
  },
  viewOnMap: {
    routeId: '',
  },
  breadcrumbsByRouteId: {},
  unsubscribeByRouteId: {},
  isBreadcrumbInitialized: false,
  stopsCountByRouteId: {},
  // One-shot signal: after creating/updating a route, the id to scroll into view
  // on whichever board is mounted. Consumed and cleared by the board once the
  // target column renders.
  scrollToRouteId: null,
  currentTab: 'sequence',
  isRouteChangesOnTemporaryMode: false,
  isDriverPathActive: false,
  isSequenceLineActive: false,
  isContainerLocationActive: false,
  // Spiderfy radius (meters) for the container markers so they ring out around
  // their stop instead of stacking on the pin. Driven off the slice (not the
  // legacy control-panel form) like isContainerLocationActive.
  containerOffset: 4,
  // Spiderfy radius (meters) for stop markers on the legacy Mapbox route map so
  // multiple WOs at the same location separate into individually clickable pins
  // (CS-2881). Driven by the map toolbar slider.
  stopMarkerOffset: 5,
  isTimelineModeActive: false,
  resetSearch: false,
  selectedSearchResult: {},
  bulkAssign: {
    isOpen: false,
    selectedRows: [],
  },
  recentStopsMoved: {
    idMap: {},
    routeId: null,
  },
  recentDraggedStop: { id: '' },
  newRouteDrawer: {
    isOpen: false,
  },
  markerDetailsDrawer: {
    isOpen: false,
    workOrderId: null,
    stopIndex: null,
    isSearchRow: false,
  },
  resequenceHistory: {
    track: false,
    zoom: '',
    coord: null,
  },
  stopDetailsDialog: {
    isOpen: false,
    id: '',
    stop: {},
    refetchStop: false,
    // Set by a List search pick: where the stop sits, for opening it when its lane isn't loaded.
    slot: null,
  },
  stopDetailsPopover: getStopDetailsPopoverClosedState(),
  bulkMoveFailure: {
    isOpen: false,
    stops: [],
  },
  polling: POLLING_DEFAULT_STATE,
  isControlPanelOpen: false,
})

const routeSlice = createSlice({
  name: 'Route',
  initialState: getInitialState(),
  reducers: {
    setSelectedSearchResult: (state, action) => {
      state.selectedSearchResult = action.payload
    },
    setResetSearch: (state, action) => {
      state.resetSearch = action.payload
    },
    setViewOnMapRouteId: (state, action) => {
      state.viewOnMap = {
        ...state.viewOnMap,
        routeId: action.payload,
      }
    },
    setAllRouteIds: (state, action) => {
      state.allRouteIds = action.payload
    },
    setAllRoutes: (state, action) => {
      state.allRoutes = action.payload
    },
    setCurrentMapLayer: (state, action) => {
      state.map = {
        ...state?.map,
        currentLayer: action.payload,
      }
    },
    setIsStreetView: (state, action) => {
      state.map = {
        ...state?.map,
        isStreetView: action.payload,
      }
    },
    toggleUnassignedStops: (state, action) => {
      state.isUnassignedStopsOpen = [true, false].includes(action.payload) ? action.payload : !state.isUnassignedStopsOpen
    },
    changeViewMode: (state, action) => {
      state.viewMode = !ROUTE_VIEW_MODES.includes(action.payload) ? DEFAULT_VIEW_MODE : action.payload
    },
    setSerializedServiceDate: (state, action) => {
      const newSerializedServiceDate = action.payload
      if (!newSerializedServiceDate) {
        state.filters.serializedServiceDate = serializeDate(new Date())
      } else if (newSerializedServiceDate instanceof Date) {
        state.filters.serializedServiceDate = serializeDate(newSerializedServiceDate)
      } else {
        state.filters.serializedServiceDate = newSerializedServiceDate
      }
    },
    setStopsCountByRouteId: (state, action) => {
      state.stopsCountByRouteId = action.payload
    },
    setScrollToRouteId: (state, action) => {
      state.scrollToRouteId = action.payload
    },
    setRouteBusinessId: (state, action) => {
      if (state?.filters?.businessId !== action.payload) {
        state.filters.selectedRouteIds = null
        state.filters.businessId = action.payload
      }
    },
    selectRouteIds: (state, action) => {
      state.filters.selectedRouteIds = Array.isArray(action.payload) ? sortBy([...action.payload]) : action.payload
    },
    changeIsRouteChangesOnTemporaryMode: (state, action) => {
      state.isRouteChangesOnTemporaryMode = action.payload
    },
    setIsDriverPathActive: (state, action) => {
      state.isDriverPathActive = action.payload
    },
    setIsSequenceLineActive: (state, action) => {
      state.isSequenceLineActive = action.payload
    },
    setIsContainerLocationActive: (state, action) => {
      state.isContainerLocationActive = action.payload
    },
    setContainerOffset: (state, action) => {
      state.containerOffset = action.payload
    },
    setStopMarkerOffset: (state, action) => {
      state.stopMarkerOffset = action.payload
    },
    setIsTimelineModeActive: (state, action) => {
      state.isTimelineModeActive = action.payload
    },
    resetRouteManagerState: state => {
      return {
        ...getInitialState(),
        isUnassignedStopsOpen: state?.isUnassignedStopsOpen,
        filters: { businessId: state?.filters?.businessId, selectedRouteIds: state?.filters?.selectedRouteIds },
        isDriverPathActive: state?.isDriverPathActive,
        isSequenceLineActive: state?.isSequenceLineActive,
        isContainerLocationActive: state?.isContainerLocationActive,
        containerOffset: state?.containerOffset ?? 4,
        stopMarkerOffset: state?.stopMarkerOffset ?? 5,
        isTimelineModeActive: false,
        map: state?.map,
        mapControlPanel: state?.mapControlPanel,
        polling: { ...state?.polling, lastRefetchTime: null },
      }
    },
    selectRowsForBulkAssign: (state, action) => {
      state.bulkAssign.selectedRows = action.payload
    },
    setIsOpenBulkAssign: (state, action) => {
      state.bulkAssign.isOpen = action.payload
    },
    setRecentStopsMoved: (state, action) => {
      state.recentStopsMoved = action.payload
    },
    setIsOpenNewRouteDrawer: (state, action) => {
      state.newRouteDrawer.isOpen = action.payload
    },
    setRecentDraggedStop: (state, action) => {
      state.recentDraggedStop = action.payload
    },
    setResequenceHistory: (state, action) => {
      state.resequenceHistory = { ...state.resequenceHistory, ...action.payload }
    },
    setStopDetailsDialogState: (state, action) => {
      state.stopDetailsDialog = { ...state.stopDetailsDialog, ...action.payload }
    },
    setStopDetailsPopoverState: (state, action) => {
      state.stopDetailsPopover = { ...state.stopDetailsPopover, ...action.payload }
    },
    // Ownership check lives here (not in callers) so a stale closure can't no-op it; no payload = force close
    closeStopDetailsPopover: (state, action) => {
      const markerId = action.payload
      if (markerId == null || state.stopDetailsPopover.id === markerId) {
        state.stopDetailsPopover = getStopDetailsPopoverClosedState()
      }
    },
    setIsBreadcrumbInitialized: (state, action) => {
      state.isBreadcrumbInitialized = action.payload
    },
    setBreadcrumbsByRouteId: (state, action) => {
      state.breadcrumbsByRouteId = { ...state.breadcrumbsByRouteId, ...action.payload }
    },
    removeBreadcrumbByRouteId: (state, action) => {
      if (action?.payload in state.breadcrumbsByRouteId) {
        state.breadcrumbsByRouteId = omit(state.breadcrumbsByRouteId, action.payload)
      }
    },
    setUnsubscribeByRouteId: (state, action) => {
      state.unsubscribeByRouteId = { ...state.unsubscribeByRouteId, ...action.payload }
    },
    resetBreadcrumbsByRouteId: state => {
      state.breadcrumbsByRouteId = {}
    },
    resetUnsubscribeByRouteId: state => {
      state.unsubscribeByRouteId = {}
    },
    setBulkMoveFailure: (state, action) => {
      state.bulkMoveFailure = {
        ...state.bulkMoveFailure,
        ...action.payload,
      }
    },
    setPollingState: (state, action) => {
      state.polling = { ...state?.polling, ...action.payload }
    },
    setMapFilters: (state, action) => {
      state.mapFilters = { ...state.mapFilters, ...action.payload }
    },
    setMapControlPanelSettings: (state, action) => {
      state.mapControlPanel = { ...state.mapControlPanel, ...action.payload }
    },
    setIsControlPanelOpen: (state, action) => {
      state.isControlPanelOpen = action.payload
    },
  },
})
export const {
  setStopsCountByRouteId,
  setScrollToRouteId,
  toggleUnassignedStops,
  changeViewMode,
  setSerializedServiceDate,
  selectRouteIds,
  changeIsRouteChangesOnTemporaryMode,
  resetRouteManagerState,
  selectRowsForBulkAssign,
  setIsOpenBulkAssign,
  setRecentStopsMoved,
  setIsOpenNewRouteDrawer,
  setCurrentMapLayer,
  setViewOnMapRouteId,
  setAllRouteIds,
  setAllRoutes,
  setResetSearch,
  setSelectedSearchResult,
  setIsStreetView,
  setRecentDraggedStop,
  setResequenceHistory,
  setStopDetailsDialogState,
  setStopDetailsPopoverState,
  closeStopDetailsPopover,
  setIsBreadcrumbInitialized,
  setIsDriverPathActive,
  setIsSequenceLineActive,
  setIsContainerLocationActive,
  setContainerOffset,
  setStopMarkerOffset,
  setIsTimelineModeActive,
  setBreadcrumbsByRouteId,
  resetBreadcrumbsByRouteId,
  setUnsubscribeByRouteId,
  resetUnsubscribeByRouteId,
  setBulkMoveFailure,
  setRouteBusinessId,
  removeBreadcrumbByRouteId,
  setPollingState,
  setMapFilters,
  setIsControlPanelOpen,
  setMapControlPanelSettings,
} = routeSlice.actions

export const selectIsBulkMoveFailureOpen = s => get(s, 'Route.bulkMoveFailure.isOpen', false)
export const selectBulkMoveFailureStops = s => get(s, 'Route.bulkMoveFailure.stops', [])

export const selectIsRouteChangesOnTemporaryMode = s => get(s, 'Route.isRouteChangesOnTemporaryMode', false)
export const selectRecentDraggedStopId = s => get(s, 'Route.recentDraggedStop.id')
export const selectRouteSerializedServiceDate = s => get(s, 'Route.filters.serializedServiceDate')
// The service date as the backend spells it (yyyy-MM-dd), or '' before one is set.
export const selectRouteServiceDateBE = s => {
  const serialized = get(s, 'Route.filters.serializedServiceDate')
  return serialized ? formatDateToBEFormatDateFns(deserializeDate(serialized)) : ''
}
export const selectRouteSelectedRouteIds = s => get(s, 'Route.filters.selectedRouteIds', null)
export const selectAllRoutes = s => get(s, 'Route.allRoutes', [])
export const selectStopDetailsDialogSlot = s => get(s, 'Route.stopDetailsDialog.slot', null)
export const selectRouteBusinessId = s => get(s, 'Route.filters.businessId', null)
export const selectPollingState = s => get(s, 'Route.polling', POLLING_DEFAULT_STATE)
export const selectStopDetailsPopoverId = s => get(s, 'Route.stopDetailsPopover.id')
export const selectIsStopDetailsPopoverHovered = s => get(s, 'Route.stopDetailsPopover.isHovered', false)
export const selectScrollToRouteId = s => get(s, 'Route.scrollToRouteId', null)
export const selectMapFilters = s => get(s, 'Route.mapFilters', {})
export const selectIsControlPanelOpen = s => get(s, 'Route.isControlPanelOpen', false)

export default routeSlice.reducer
